import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import {
  activeWear,
  isSeasonId,
  isValidRoomCode,
  setSeasonOverride,
  type GameId,
  type Progress,
} from '@ofc/shared';
import {
  HELP_EVENT,
  LoungeLink,
  NamePrompt,
  KbDebug,
  PLAYER_CARD_EVENT,
  RENAME_EVENT,
  applySkin,
  useWakeLock,
  type PlayerCardRequest,
} from '@ofc/ui';
import { useAccount } from './auth';
import { AccountContext } from './account-context';
import { Landing, gameTitle } from './screens/Landing';
import { HowToPlay, hasSeenHowTo } from './components/HowToPlay';
import { UpdateBanner } from './components/UpdateBanner';
import { useLounge } from './lounge/useLounge';
import { Loading } from './components/Loading';
import { prefetchGame, screenLoaders } from './loaders';
import '@ofc/ui/theme.css';

const Room = lazy(() => screenLoaders.movie().then((m) => ({ default: m.Room })));
const QuizRoomScreen = lazy(() => screenLoaders.quiz().then((m) => ({ default: m.QuizRoomScreen })));
const QuiplashRoomScreen = lazy(() => screenLoaders.quiplash().then((m) => ({ default: m.QuiplashRoomScreen })));
const FibbageRoomScreen = lazy(() => screenLoaders.fibbage().then((m) => ({ default: m.FibbageRoomScreen })));
const EnoughRoomScreen = lazy(() => screenLoaders.enough().then((m) => ({ default: m.EnoughRoomScreen })));
const WaveRoomScreen = lazy(() => screenLoaders.wavelengths().then((m) => ({ default: m.WaveRoomScreen })));
const ObscurityRoomScreen = lazy(() => screenLoaders.obscurity().then((m) => ({ default: m.ObscurityRoomScreen })));
const SuperlativesRoomScreen = lazy(() =>
  screenLoaders.superlatives().then((m) => ({ default: m.SuperlativesRoomScreen })),
);
const ChameleonRoomScreen = lazy(() => screenLoaders.chameleon().then((m) => ({ default: m.ChameleonRoomScreen })));
const HotTakesRoomScreen = lazy(() => screenLoaders.hottakes().then((m) => ({ default: m.HotTakesRoomScreen })));
const ArcadeRoomScreen = lazy(() => screenLoaders.connect4().then((m) => ({ default: m.ArcadeRoomScreen })));
const LoungeScreen = lazy(() => import('./lounge/LoungeScreen').then((m) => ({ default: m.LoungeScreen })));
const TvScreen = lazy(() => import('./tv/Tv').then((m) => ({ default: m.TvScreen })));
const AudienceScreen = lazy(() => import('./tv/AudienceScreen').then((m) => ({ default: m.AudienceScreen })));
const DailyScreen = lazy(() => import('./daily/DailyScreen').then((m) => ({ default: m.DailyScreen })));
const PlayerCardSheet = lazy(() =>
  import('./components/PlayerCardSheet').then((m) => ({ default: m.PlayerCardSheet })),
);

// On a dev server only, `localStorage['ofc:season']` pretends an event is on, to
// try the seasonal things out. Production never reads it.
try {
  const forced = localStorage.getItem('ofc:season');
  const local = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  if (local && isSeasonId(forced)) setSeasonOverride(forced);
} catch {
  // Storage blocked: nothing to pretend.
}

/**
 * Hash routing: no router dependency, and a shared link survives a cold load
 * without needing any server-side route handling.
 *
 *   #/                       landing
 *   #/movie/r/ABCD           a game's room (movie, quiz, quiplash, fibbage,
 *                            enough, wavelengths, obscurity, superlatives)
 *   #/quiz/r/ABCD/l/WXYZ     the same, as part of game night WXYZ
 *   #/l/WXYZ                 game night WXYZ's lounge
 *   #/tv/ABCD                this screen as the big shared one
 *   #/quiplash/a/ABCD        one of the audience in a voting game
 *   #/daily                  the daily challenge
 *   #/r/ABCD                 legacy movie link, still honoured
 */
type Route =
  | { kind: 'landing' }
  | { kind: 'room'; game: GameId; code: string; lounge: string | null }
  | { kind: 'lounge'; code: string }
  | { kind: 'tv'; code: string }
  | { kind: 'audience'; game: GameId; code: string }
  | { kind: 'daily' };

const GAME_PATTERN =
  'movie|quiz|quiplash|fibbage|enough|wavelengths|obscurity|superlatives|chameleon|hottakes|' +
  'higherlower|castreveal|posterzoom|sixdegrees|connect4|noughts|battleships|dots|wordduel|anagrams|poker|liarsdice|whosaid|wouldyourather|cheat|' +
  'rankem|spotlight|blindranking|onestar|wagerquiz|fakinit|undercover|mole|boxart';

function readRoute(): Route {
  const hash = location.hash;
  if (hash === '#/daily') return { kind: 'daily' };
  const valid = (raw: string | undefined) => {
    const code = (raw ?? '').toUpperCase();
    return isValidRoomCode(code) ? code : null;
  };

  const scoped = new RegExp(`^#/(${GAME_PATTERN})/r/([A-Za-z0-9]+)(?:/l/([A-Za-z0-9]+))?$`).exec(hash);
  if (scoped) {
    const code = valid(scoped[2]);
    if (code) return { kind: 'room', game: scoped[1] as GameId, code, lounge: valid(scoped[3]) };
  }

  const audience = /^#\/(quiplash|fibbage|enough|superlatives|chameleon)\/a\/([A-Za-z0-9]+)$/.exec(hash);
  if (audience) {
    const code = valid(audience[2]);
    if (code) return { kind: 'audience', game: audience[1] as GameId, code };
  }

  const lounge = /^#\/l\/([A-Za-z0-9]+)$/.exec(hash);
  if (lounge) {
    const code = valid(lounge[1]);
    if (code) return { kind: 'lounge', code };
  }

  const tv = /^#\/tv\/([A-Za-z0-9]+)$/.exec(hash);
  if (tv) {
    const code = valid(tv[1]);
    if (code) return { kind: 'tv', code };
  }

  // Links shared before the quiz existed carry no game segment.
  const legacy = /^#\/r\/([A-Za-z0-9]+)$/.exec(hash);
  if (legacy) {
    const code = valid(legacy[1]);
    if (code) return { kind: 'room', game: 'movie', code, lounge: null };
  }

  return { kind: 'landing' };
}

const NAME_KEY = 'movie-game:name';

export function App() {
  const account = useAccount();
  const [route, setRoute] = useState<Route>(readRoute);
  const [guestName, setGuestName] = useState<string>(() => {
    try {
      return localStorage.getItem(NAME_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [baseline, setBaseline] = useState<Progress | null>(null);
  const [howTo, setHowTo] = useState<GameId | null>(null);
  const [card, setCard] = useState<PlayerCardRequest | null>(null);

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // A signed-in player always plays under their account name.
  const name = account.session?.user.username ?? guestName;
  const token = account.session?.token;
  const roomGame = route.kind === 'room' ? route.game : null;

  // Every room, every game: a phone left on the table must not lock mid-round.
  useWakeLock(route.kind === 'room' || route.kind === 'lounge');

  // Your skin follows your account, and a game can have one of its own.
  const user = account.session?.user;
  // An event's skin, while it is on, is worn everywhere over the usual choice.
  const eventSkin = activeWear(user?.seasonal)?.skin;
  const skin = eventSkin || (roomGame && user?.gameSkins?.[roomGame]) || user?.skin || 'neon';
  useEffect(() => applySkin(skin), [skin]);

  // What a player had before a game starts, so its end can say what it was worth.
  const roomKey = route.kind === 'room' ? `${route.game}:${route.code}` : null;
  const loadProgress = account.loadProgress;
  useEffect(() => {
    if (!roomKey || !token) {
      setBaseline(null);
      return;
    }
    let cancelled = false;
    void loadProgress().then((p) => !cancelled && setBaseline(p));
    return () => {
      cancelled = true;
    };
    // Re-read for each room, not for each re-render of the account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomKey, token]);

  // How to play: by itself the first time on this phone, and from the ? after.
  // Leaving the game puts its slides away: on a game night the host can move
  // everybody on while they are still open, and the last game's slides would sit
  // over the next one, taking the first tap.
  useEffect(() => {
    setHowTo(roomGame && !hasSeenHowTo(roomGame) ? roomGame : null);
  }, [roomGame]);
  useEffect(() => {
    const onHelp = () => roomGame && setHowTo(roomGame);
    window.addEventListener(HELP_EVENT, onHelp);
    return () => window.removeEventListener(HELP_EVENT, onHelp);
  }, [roomGame]);

  // Somebody's card, from a tap on their name anywhere.
  useEffect(() => {
    const onCard = (event: Event) => setCard((event as CustomEvent<PlayerCardRequest>).detail);
    window.addEventListener(PLAYER_CARD_EVENT, onCard);
    return () => window.removeEventListener(PLAYER_CARD_EVENT, onCard);
  }, []);
  // A card opened in one room is put away when you are moved on to the next.
  useEffect(() => setCard(null), [route.kind, roomKey]);

  // Game night: one socket for the whole evening, wherever in it you are.
  const loungeCode = route.kind === 'lounge' ? route.code : route.kind === 'room' ? route.lounge : null;
  const goTo = useCallback(
    (game: GameId, code: string) => {
      prefetchGame(game);
      if (loungeCode) location.hash = `#/${game}/r/${code}/l/${loungeCode}`;
    },
    [loungeCode],
  );
  const goHome = useCallback(() => {
    if (loungeCode) location.hash = `#/l/${loungeCode}`;
  }, [loungeCode]);
  const lounge = useLounge(name.trim() ? loungeCode : null, name, token, goTo, { onHome: goHome });
  const loungeLink = useMemo(
    () =>
      loungeCode
        ? { code: loungeCode, open: goHome, host: lounge.isHost, recall: () => lounge.actions.recall() }
        : null,
    [loungeCode, goHome, lounge.isHost, lounge.actions],
  );

  const accountValue = useMemo(() => ({ account, baseline, setBaseline }), [account, baseline]);

  const remember = (playerName: string) => {
    try {
      localStorage.setItem(NAME_KEY, playerName);
    } catch {
      // Storage blocked; the name just will not be remembered next time.
    }
    setGuestName(playerName);
  };

  // A room said the name is taken and a new one was picked: play under that.
  useEffect(() => {
    const rename = (event: Event) => remember((event as CustomEvent<string>).detail);
    window.addEventListener(RENAME_EVENT, rename);
    return () => window.removeEventListener(RENAME_EVENT, rename);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enter = (game: GameId | 'lounge', code: string, playerName: string) => {
    prefetchGame(game);
    remember(playerName);
    location.hash = game === 'lounge' ? `#/l/${code}` : `#/${game}/r/${code}`;
  };

  // Out of a game night's game is back to the night; out of the night is home.
  const leave = () => {
    location.hash = route.kind === 'room' && route.lounge ? `#/l/${route.lounge}` : '#/';
  };

  const overlays = (
    <>
      <KbDebug />
      {(route.kind === 'landing' || route.kind === 'lounge') && <UpdateBanner />}
      {howTo && <HowToPlay game={howTo} onClose={() => setHowTo(null)} />}
      {card && (
        <Suspense fallback={null}>
          <PlayerCardSheet
            request={card}
            sessionToken={token}
            viewerId={account.session?.user.id}
            onClose={() => setCard(null)}
          />
        </Suspense>
      )}
    </>
  );

  if (route.kind === 'tv') {
    return (
      <Suspense fallback={<Loading />}>
        <TvScreen code={route.code} onLeave={leave} />
      </Suspense>
    );
  }

  if (route.kind === 'daily') {
    return (
      <AccountContext.Provider value={accountValue}>
        <Suspense fallback={<Loading />}>
          <DailyScreen account={account} onLeave={leave} />
        </Suspense>
        {overlays}
      </AccountContext.Provider>
    );
  }

  if (route.kind === 'landing') {
    return (
      <AccountContext.Provider value={accountValue}>
        <Landing
          account={account}
          initialName={name}
          onEnter={enter}
          onTv={(code) => (location.hash = `#/tv/${code}`)}
        />
        {overlays}
      </AccountContext.Provider>
    );
  }

  // Opening a shared link with no name yet: ask before joining, so nobody lands in
  // the game as an anonymous "Player".
  if (!name.trim()) {
    return (
      <NamePrompt
        roomCode={route.code}
        gameTitle={route.kind === 'lounge' ? 'game night' : gameTitle(route.game)}
        onName={(chosen: string) => {
          remember(chosen);
          // Same address: setting the name is enough to carry on into it.
        }}
        onCancel={leave}
      />
    );
  }

  if (route.kind === 'audience') {
    return (
      <Suspense fallback={<Loading />}>
        <AudienceScreen key={route.code} game={route.game} code={route.code} name={name} onLeave={leave} />
      </Suspense>
    );
  }

  if (route.kind === 'lounge') {
    return (
      <AccountContext.Provider value={accountValue}>
        <Suspense fallback={<Loading />}>
          <LoungeScreen
            code={route.code}
            lounge={lounge}
            onLeave={leave}
            onRejoin={(game, code) => (location.hash = `#/${game}/r/${code}/l/${route.code}`)}
          />
        </Suspense>
        {overlays}
      </AccountContext.Provider>
    );
  }

  const props = { roomCode: route.code, name, sessionToken: token, onLeave: leave };
  const screen = (() => {
    switch (route.game) {
      case 'chameleon':
        return <ChameleonRoomScreen {...props} />;
      case 'hottakes':
        return <HotTakesRoomScreen {...props} />;
      case 'higherlower':
      case 'castreveal':
      case 'posterzoom':
      case 'sixdegrees':
      case 'connect4':
      case 'noughts':
      case 'battleships':
      case 'dots':
      case 'wordduel':
      case 'anagrams':
      case 'poker':
      case 'liarsdice':
      case 'whosaid':
      case 'wouldyourather':
      case 'cheat':
      case 'rankem':
      case 'spotlight':
      case 'blindranking':
      case 'onestar':
      case 'wagerquiz':
      case 'fakinit':
      case 'undercover':
      case 'mole':
      case 'boxart':
        return <ArcadeRoomScreen game={route.game} {...props} />;
      case 'superlatives':
        return <SuperlativesRoomScreen {...props} />;
      case 'obscurity':
        return <ObscurityRoomScreen {...props} />;
      case 'wavelengths':
        return <WaveRoomScreen {...props} />;
      case 'enough':
        return <EnoughRoomScreen {...props} />;
      case 'fibbage':
        return <FibbageRoomScreen {...props} />;
      case 'quiplash':
        return <QuiplashRoomScreen {...props} />;
      case 'quiz':
        return <QuizRoomScreen {...props} />;
      default:
        return <Room {...props} />;
    }
  })();

  return (
    <AccountContext.Provider value={accountValue}>
      <LoungeLink.Provider value={loungeLink}>
        {/* Keyed by room, so moving from one game of the night to the next
            starts the new one fresh rather than reusing the last one's state. */}
        <div key={`${route.game}:${route.code}:${name}`} style={{ display: 'contents' }}>
          <Suspense fallback={<Loading />}>{screen}</Suspense>
        </div>
        {overlays}
      </LoungeLink.Provider>
    </AccountContext.Provider>
  );
}
